import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { Badge } from "@shared/ui/badge";
import {
  Search,
  Calendar,
  Zap,
  MapPin,
  User,
  Users,
  Loader2,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";
import {
  useJobCardAssignment,
  useFieldForceRecommend,
} from "../../../../../hooks";
import {
  RecommendedTechnician,
  SingleTechnicianAssignmentModalProps,
} from "@features/wx/dispatcher/types";
import { logEvent } from "@shared/analytics/analytics";
import { fieldForce } from "@features/wx/settings/components";
import { useGlobalUserProfile } from "@shared/hooks/useGlobalUser";

// Helper function to extract coordinates from service order
const extractMeterLocations = (serviceOrder: any): string[] => {
  if (!serviceOrder) return [];

  // Try to extract coordinates from various possible fields
  const coordinates = [];

  // Check if serviceOrder has direct coordinates
  if (serviceOrder.latitude && serviceOrder.longitude) {
    coordinates.push(`(${serviceOrder.latitude}, ${serviceOrder.longitude})`);
  }

  // Check if serviceOrder has location data
  if (serviceOrder.location?.lat && serviceOrder.location?.lng) {
    coordinates.push(
      `(${serviceOrder.location.lat}, ${serviceOrder.location.lng})`
    );
  }

  // Check if serviceOrder has meter data with coordinates
  if (serviceOrder.meter?.coordinates) {
    const { lat, lng } = serviceOrder.meter.coordinates;
    if (lat && lng) {
      coordinates.push(`(${lat}, ${lng})`);
    }
  }

  return coordinates;
};

// Transform API data to match existing UI expectations
const transformTechnicianData = (technicians: RecommendedTechnician[]) => {
  return technicians.map((tech) => ({
    id: tech.fieldForceId,
    name: tech.name,
    workload: tech.workLoad,
    distance: tech.distance,
    skills: tech.skill || [],
    skillMatch: tech.skillMatch,
    status:
      tech.availabilityScore >= 100
        ? "available"
        : tech.workLoad >= 80
        ? "overloaded"
        : "busy",
    utilization: tech.workLoad || 0,
    rating: tech.rating || 0,
    bestMatch: tech.matchPercentage || 0,
    tagline: tech.recommendedReason || `${tech.roleName} specialist`,
    fieldForceNo: tech.fieldForceNo,
    contactNumber: tech.contactNumber,
    email: tech.email,
    originalData: tech,
  }));
};

export const SingleTechnicianAssignmentModal: React.FC<
  SingleTechnicianAssignmentModalProps
> = ({ open, onOpenChange, serviceOrder }) => {
  // Server-side search state management
  const [searchData, setSearchData] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [selectedTechnician, setSelectedTechnician] = useState<any>(null);
  const itemsPerPage = 5;
  const [currentPage, setCurrentPage] = useState(1);
  const [isAutoAssign, setIsAutoAssign] = useState(false);
const { data:user } = useGlobalUserProfile();
  // API hooks
  const assignmentMutation = useJobCardAssignment();

  // Extract meter locations and SOP ID from service order
  const meterLocations = useMemo(() => {
    return extractMeterLocations(serviceOrder);
  }, [serviceOrder]);

  const sopId = serviceOrder?.sop || "";

  // Debounced search function
  const debouncedSearch = useCallback(() => {
    let timeoutId: NodeJS.Timeout;
    return (searchTerm: string) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        console.log(
          "🔍 Single assignment debounced search triggered:",
          searchTerm
        );
        setSearchData(searchTerm);
      }, 500);
    };
  }, []);

  // Reset search when modal opens/closes
  useEffect(() => {
    if (open) {
      setSearchData("");
      setSearchInputValue("");
      setSelectedTechnician(null);
    }
  }, [open]);

  // API call for field force recommendations with server-side search
  const {
    data: recommendationData,
    isLoading: loadingTechnicians,
    error: technicianError,
    refetch: refetchTechnicians,
  } = useFieldForceRecommend(
    {
      sopId: sopId.toString(),
      meterLocations: meterLocations,
      searchData: searchData || "",
      page: currentPage,
      limit: itemsPerPage,
    },
    {
      enabled: !!sopId && open,
    }
  );

  const totalCount = recommendationData?.count || 0;
  const pagination = useMemo(() => {
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalCount);

    return {
      currentPage,
      totalPages,
      totalItems: totalCount,
      itemsPerPage,
      startItem,
      endItem,
    };
  }, [totalCount, currentPage, itemsPerPage]);

  useEffect(() => {
    if (open) {
      setSearchData("");
      setSearchInputValue("");
      setSelectedTechnician(null);
      setCurrentPage(1);
    }
  }, [open]);

  const debouncedSearchFn = useMemo(() => {
    let timeoutId: NodeJS.Timeout;
    return (searchTerm: string) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        console.log(
          "🔍 Single assignment debounced search triggered:",
          searchTerm
        );
        setSearchData(searchTerm);
        setCurrentPage(1); // Reset to first page on search
      }, 500);
    };
  }, []);

  // Transform API data or use empty array - NO CLIENT-SIDE FILTERING
  const technicians = recommendationData?.results
    ? transformTechnicianData(recommendationData.results)
    : [];

  const getWorkloadColor = (workload: number) => {
    if (workload === 0) return "text-green-600";
    if (workload >= 4) return "text-red-600";
    return "text-orange-600";
  };

  const handleAssign = async () => {
    if (!selectedTechnician || !serviceOrder) return;

    try {
      await assignmentMutation.mutateAsync({
        job_card_ids: [serviceOrder.id],
        field_force_id: selectedTechnician.id,
        assigned_by:user?.user.name,
      }, {
        onSuccess: () => {
          logEvent("Technician Assigned", {
            assignmentMethod: isAutoAssign ? 'auto': 'manual',
            technicianId: selectedTechnician.id,

          })
        }
      });

      onOpenChange(false);
      setSelectedTechnician(null);
    } catch (error) {}
  };

  const handleAutoAssign = () => {
    if (technicians.length === 0) return;

    // Find best technician based on workload and distance
    setIsAutoAssign(true);
    const availableTechnicians = technicians.filter(
      (tech) => tech.status === "available"
    );

    if (availableTechnicians.length === 0) return;

    const bestMatch = availableTechnicians.reduce((best: any, current: any) => {
      // Prioritize by lowest workload, then by distance
      if (current.workload < best.workload) return current;
      if (
        current.workload === best.workload &&
        parseFloat(current.distance) < parseFloat(best.distance)
      ) {
        return current;
      }
      return best;
    });

    setSelectedTechnician(bestMatch);
  };

  const handleCancel = () => {
    onOpenChange(false);
    setSelectedTechnician(null);
  };

  const handleRetry = () => {
    refetchTechnicians();
  };

  if (!serviceOrder) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col p-0">
        <DialogHeader className="border-b px-6 py-4 flex-shrink-0">
          <DialogTitle className="text-xl font-semibold">
            Assign Technician - {serviceOrder.name}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Left Panel - Search and Filters */}
          <div className="w-2/5 border-r p-6 space-y-6 flex-shrink-0">
            {/* Server-side Search Bar */}
            <div className="py-0">
              <label className="block text-sm font-medium mb-2">
                Search Technicians
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by name, ID, or skills..."
                  value={searchInputValue}
                  onChange={(e) => {
                    setSearchInputValue(e.target.value);
                    debouncedSearchFn(e.target.value);
                  }}
                  className="pl-9"
                  disabled={loadingTechnicians}
                />
                {loadingTechnicians && (
                  <Loader2 className="absolute right-3 top-2.5 h-4 w-4 text-gray-400 animate-spin" />
                )}
              </div>
            </div>

            {/* Expected Date */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="h-4 w-4 text-gray-600" />
                <h3 className="font-medium">Expected Date</h3>
              </div>
              <p className="text-sm font-medium">
                {serviceOrder.expectedEndDate}
              </p>
              <p className="text-xs text-gray-600">
                {`Due in ${serviceOrder.expectedEndDate} days`}
              </p>
            </div>

            {/* Required Skills */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Zap className="h-4 w-4 text-gray-600" />
                <h3 className="font-medium">Required Skills</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {serviceOrder.requiredSkills?.map((skill: string) => (
                  <Badge
                    key={skill}
                    className="bg-blue-100 text-blue-800 text-xs"
                  >
                    {skill}
                  </Badge>
                )) || (
                  <>
                    <Badge className="bg-blue-100 text-blue-800 text-xs">
                      {serviceOrder.serviceAssociation}
                    </Badge>
                    <Badge className="bg-blue-100 text-blue-800 text-xs">
                      Field Work
                    </Badge>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Right Panel - Summary and Available Technicians */}
          <div className="w-3/5 flex flex-col min-h-0">
            {/* Summary Section */}
            <div className="bg-gray-50 p-6 flex-shrink-0 px-[24px] py-[8px]">
              <h3 className="font-medium mb-4">Summary</h3>
              <div className="grid grid-cols-2 gap-4 py-0 px-0">
                <div className="bg-white rounded-lg p-4 border">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Service Orders</p>
                      <p className="text-2xl font-bold">1</p>
                    </div>
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <User className="h-5 w-5 text-blue-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4 border">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Available Techs</p>
                      <p className="text-2xl font-bold">{technicians.length}</p>
                    </div>
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Users className="h-5 w-5 text-green-600" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Available Technicians */}
            <div className="flex-1 flex flex-col min-h-0 p-6 py-[5px]">
              <div className="flex items-center justify-between mb-4 flex-shrink-0">
                <h3 className="font-medium">
                  Available Technicians
                  {searchData && (
                    <span className="ml-2 text-xs text-gray-500">
                      ({totalCount} results for "{searchData}")
                    </span>
                  )}
                  {!searchData && totalCount > 0 && (
                    <span className="ml-2 text-xs text-gray-500">
                      (Showing {pagination.startItem}-{pagination.endItem} of{" "}
                      {totalCount})
                    </span>
                  )}
                </h3>
                {technicians.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAutoAssign}
                    className="bg-orange-500 text-white hover:bg-orange-600"
                    disabled={loadingTechnicians}
                  >
                    <Zap className="h-4 w-4 mr-1" />
                    Auto-Assign
                  </Button>
                )}
              </div>

              {technicianError ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center text-red-600">
                    <Users className="h-8 w-8 mx-auto mb-2 text-red-300" />
                    <p className="text-sm mb-2">Failed to load technicians</p>
                    <Button variant="outline" size="sm" onClick={handleRetry}>
                      Try Again
                    </Button>
                  </div>
                </div>
              ) : technicians.length === 0 ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center text-gray-500">
                    <Users className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">
                      {searchData
                        ? `No technicians found matching "${searchData}"`
                        : "No technicians available"}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto space-y-3 pr-2">
                  {technicians.map((technician: any) => (
                    <div
                      key={technician.id}
                      className={`p-4 rounded-lg border cursor-pointer transition-all ${
                        selectedTechnician?.id === technician.id
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                      }`}
                      onClick={() => setSelectedTechnician(technician)}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-medium text-sm">
                            {technician.name
                              .split(" ")
                              .map((n: string) => n[0])
                              .join("")}
                          </div>
                          <div>
                            <p className="font-medium">{technician.name}</p>
                            <p className="text-sm text-gray-500">
                              {technician.code}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 text-gray-500">
                          <MapPin className="h-4 w-4" />
                          <span className="text-sm font-medium">
                            {technician.distance}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-1 mb-3">
                        <Users className="h-4 w-4 text-gray-500" />
                        <span className="text-sm text-gray-500">
                          Workload:{" "}
                        </span>
                        <span
                          className={`font-medium text-sm ${getWorkloadColor(
                            technician.workload
                          )}`}
                        >
                          {technician.workload}
                        </span>
                      </div>

                      <div>
                        <div className="flex items-center gap-1 mb-2">
                          <Zap className="h-4 w-4 text-gray-500" />
                          <p className="text-sm text-gray-500">Skills:</p>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {technician.skills
                            .slice(0, 2)
                            .map((skill: string) => (
                              <Badge
                                key={skill}
                                variant="outline"
                                className="text-xs px-2 py-1"
                              >
                                {skill}
                              </Badge>
                            ))}
                          {technician.skills.length > 2 && (
                            <Badge
                              variant="outline"
                              className="text-xs px-2 py-1"
                            >
                              +{technician.skills.length - 2}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        {
          <div className="flex items-center justify-center gap-2 pt-4 mt-4 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>

            {/* Page numbers */}
            {Array.from(
              { length: Math.min(5, pagination.totalPages) },
              (_, i) => {
                const startPage = Math.max(1, currentPage - 2);
                const endPage = Math.min(pagination.totalPages, startPage + 4);
                const adjustedStartPage = Math.max(1, endPage - 4);
                const page = adjustedStartPage + i;

                if (page > pagination.totalPages) return null;

                return (
                  <Button
                    key={page}
                    variant={currentPage === page ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCurrentPage(page)}
                    className={`w-8 h-8 p-0 ${
                      currentPage === page
                        ? "bg-blue-600 text-white"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {page}
                  </Button>
                );
              }
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setCurrentPage(Math.min(pagination.totalPages, currentPage + 1))
              }
              disabled={currentPage === pagination.totalPages}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        }

        <DialogFooter className="border-t px-6 py-4 flex-shrink-0">
          <div className="flex gap-3 w-full justify-end">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              onClick={handleAssign}
              disabled={!selectedTechnician || assignmentMutation.isPending}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {assignmentMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Assigning...
                </>
              ) : (
                <>
                  <User className="h-4 w-4 mr-2" />
                  Assign to 1 Order
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
